// @flow

import type {Element} from 'react';
import type {MultiRoom} from 'redux/v1/journey/models';

import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

type Attribute = {
  label: string,
  type: string,
  icon?: Element<any>,
  description: string,
};

type ParsedRoom = {
  roomCount: number,
  description: string,
  roomType: string,
  roomAttributes: Attribute[],
  code: string,
  catering?: string,
  cateringDescription?: string,
  cateringCode?: string,
};

export const useParsedMultiRooms = (rooms: MultiRoom[]) => {
  const {t} = useTranslation(['inviaTypes', 'travelDetailsOverlay']);
  const roomTypeMap = useMemo(() => t('roomType', {returnObjects: true}), [t]);
  const roomAttributesMap = useMemo(
    () => t('inviaTypes:roomAttributes', {returnObjects: true}),
    [t]
  );
  const roomViewsMap = useMemo(
    () => t('inviaTypes:roomViews', {returnObjects: true}),
    [t]
  );

  const roomAttributes = useMemo(
    () => ({...roomAttributesMap, ...roomViewsMap}),
    [roomAttributesMap, roomViewsMap]
  );

  const boardTypeDescriptions = {
      'IT06-NB': 'travelOverlay:boardTypeDescription.noBoard',
      'IT06-BB': 'travelOverlay:boardTypeDescription.breakfast',
      'IT06-HB': 'travelOverlay:boardTypeDescription.halfBoard',
      'IT06-FB': 'travelOverlay:boardTypeDescription.fullBoard',
      'IT06-AI': 'travelOverlay:boardTypeDescription.allInclusive',
    }

  return useMemo(() => {
    const roomCounter: {[string]: ParsedRoom} = {};
    const attributeList = {};
    const cateringCodeList = {};

    if (rooms) for (const room of rooms) {
      const adjustedAttributes = room.attributes
        .sort()
        .reduce((accumulator, attribute) => {
          if (roomAttributes[attribute])
            accumulator.push({
              label: roomAttributes[attribute],
              type: attribute,
              description: '',
            });
          return accumulator;
        }, []);
      const attributesKey = adjustedAttributes
        .map((attribute) => attribute.type)
        .join('');
      const key = `${room.roomType}_${room.boardType}_${attributesKey}_${room.description}_${room.code}`;
      const primaryBoardType = room.boardType?.split('#')[0] ?? '';

      roomCounter[key]
        ? (roomCounter[key].roomCount += 1)
        : (roomCounter[key] = {
            roomCount: 1,
            description: room.description,
            roomType: roomTypeMap[room.roomType] || roomTypeMap[''],
            roomAttributes: adjustedAttributes,
            code: room.code,
            catering: t(`inviaTypes:boardType.${room.boardType}`),
            cateringCode: room.boardType,
            cateringDescription:
              boardTypeDescriptions[primaryBoardType] &&
              t(
                `${boardTypeDescriptions[primaryBoardType]}`
              ),
          });
      for (const attribute of adjustedAttributes) {
        attributeList[attribute.type] =
          (attributeList[attribute.type] || 0) + 1;
      }

      cateringCodeList[room.boardType] =
        (cateringCodeList[room.boardType] || 0) + 1;
    }

    const commonCateringCode = Object.keys(cateringCodeList).find(
      (key) => cateringCodeList[key] === rooms.length
    );
    return {
      parsedRooms: Object.values(roomCounter).map(
        ({
          roomAttributes,
          catering,
          cateringDescription,
          cateringCode,
          ...rest
        }) => ({
          roomAttributes: roomAttributes.filter(
            (item) => attributeList[item.type] !== rooms.length
          ),
          ...rest,
          ...(cateringCode === commonCateringCode
            ? {}
            : {catering, cateringDescription, cateringCode}),
        })
      ),
      globalAttributes: Object.keys(attributeList).reduce((accumulator, key) => {
        if (attributeList[key] === rooms.length) {
          accumulator.push({
            label: roomAttributes[key],
            type: key,
            description: '',
          });
        }
        return accumulator;
      }, []),
    };
  }, [boardTypeDescriptions, roomAttributes, roomTypeMap, rooms, t]);
};
